import React from "react";

import clsx from "clsx";
import { Link, useLocation } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import four04 from "@assets/404.gif";
import loadingScreenNoText from "@assets/loading-screen-no-text.gif";
import loadingScreen from "@assets/page-loading.gif";
import loadingScreenAtom from "@assets/sales-v3-chart-loading.gif";
import { LogOnMount } from "@components/v2/analytics";
import { PrimaryButton } from "@shared/buttons";

import { useAuth0 } from "./auth0-wrapper";
import { useUser } from "./user-context";

export interface TitleSlideProps {
  className?: string;
  children?: React.ReactNode | React.ReactNode[];
  style?: React.CSSProperties;
}

export function TitleSlide(props: TitleSlideProps) {
  return (
    <div
      className={clsx(
        props.className,
        "flex items-center title-slide w-100 h-100",
      )}
      style={props.style}
    >
      <div className="mx-auto">{props.children}</div>
    </div>
  );
}

export const LoadingSlide = ({
  className,
  text,
}: {
  className?: string;
  text?: string;
}) => (
  <TitleSlide>
    <img
      src={loadingScreen}
      alt="Loading screen gif"
      className={clsx("w-1/2 m-auto", className)}
    />
    {text && (
      <div className={twMerge("text-2xl text-center  text-interstellar")}>
        {text}
      </div>
    )}
  </TitleSlide>
);

// Use this as a WHOLE PAGE loading screen
export const LoadingScreen = ({ className }: { className?: string }) => (
  <TitleSlide className="bg-v2-secondary relative">
    <header className="w-full h-14 absolute top-0 left-0 bg-v2-primary border-b border-v2-tertiary" />
    <div className="h-[calc(100%-56px)] mt-14 fixed top-0 left-0 w-14 text-white bg-interstellar" />
    <img
      src={loadingScreen}
      alt="Loading screen"
      className={clsx("w-1/2 m-auto", className)}
    />
  </TitleSlide>
);

export const LoadingSlideNoText = ({ className }: { className?: string }) => (
  <TitleSlide>
    <img
      src={loadingScreenNoText}
      alt="Loading screen gif"
      className={clsx("w-1/2 m-auto", className)}
    />
  </TitleSlide>
);

export const LoadingSlideAtom = ({ className }: { className?: string }) => (
  <TitleSlide>
    <img
      src={loadingScreenAtom}
      alt="Chart loading atom gif"
      className={clsx("w-2/5 m-auto", className)}
    />
  </TitleSlide>
);

export const Loading = ({ className }: { className?: string }) => {
  return (
    <div
      className={clsx("absolute w-36 left-[50%] top-[50%] z-[1]", className)}
      style={{ transform: "translate(-50%, -50%)" }}
    >
      <LoadingSlideNoText className="w-auto" />
    </div>
  );
};

export function NoDataView(props: TitleSlideProps) {
  const { user } = useUser();

  if (user.isAdmin) {
    return <AdminView {...props} />;
  }

  return (
    <TitleSlide {...props}>
      <h3 className="text-center">
        No data.{" "}
        <span role="img" aria-label="frown">
          🙁
        </span>
      </h3>
      <p className="mt-2 text-center">
        Please add some integrations in the settings page and then get in touch
        with the Northbeam team!
      </p>
    </TitleSlide>
  );
}

export function AdminView(props: TitleSlideProps) {
  return (
    <TitleSlide {...props}>
      <h3 className="text-center">
        Hello admin!{" "}
        <span role="img" aria-label="cowboy">
          🤠
        </span>
      </h3>
      <p className="mt-2 text-center">
        Choose a client to look at from the navigation bar at the top of this
        page.
      </p>
    </TitleSlide>
  );
}

export function Page404(props: TitleSlideProps) {
  const location = useLocation();
  const { logout } = useAuth0();
  const { user } = useUser();

  return (
    <TitleSlide {...props} className={clsx(props.className, "bg-cosmic-5")}>
      <LogOnMount name="Page not found" data={location} />
      <div className="ml-12 h-[calc(100vh-56px)] w-[calc(100vw-56px)] relative">
        <div
          className={clsx(
            "absolute-horizontal-center top-[10%] px-8 w-full",
            "flex flex-col items-center space-y-3",
          )}
        >
          <img src={four04} alt="404" className="w-full max-w-2xl" />
          <h1 className="font-bold text-4xl">We can&apos;t find this page</h1>
          <div>
            <h3 className="font-bold text-2xl">
              It&apos;s possible you&apos;re not logged in to the correct
              account.
            </h3>
            <p className="text-center mb-2">
              If this is an unexpected result, please contact us at your
              convenience.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <a
              className="button primary-button sm no-underline font-bold"
              href="mailto:success@northbeam.io"
              target="_blank"
              rel="noreferrer"
            >
              CONTACT US
            </a>
            <PrimaryButton
              className="font-bold"
              onClick={() =>
                logout({ logoutParams: { returnTo: window.location.origin } })
              }
            >
              LOG OUT
            </PrimaryButton>
          </div>
          <Link
            className="button tertiary-button sm no-underline font-bold"
            to={user.pathFromRoot("overview")}
          >
            OR RETURN TO THE HOME SCREEN
          </Link>
        </div>
      </div>
    </TitleSlide>
  );
}
